import React, { ReactNode, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import './Abookpicker.scss';
import { Popover } from '@mantine/core';
import { useMergedRef } from '@mantine/hooks';
import { MdClose, MdMenu, MdOutlineExpandMore } from 'react-icons/md';
import Input from '@srm/ui/input';
import clsx from 'clsx';
import AbookpickerModal from './AbookpickerModal/AbookpickerModal';
import AbookCombobox from './AbookCombobox/AbookCombobox';
import AbookItemAvatar from './AbookCombobox/AbookItemAvatar';
import { isPersonTab } from '@utils/abookTabVisibility';
import PickerSkeleton from '@atoms/PickerCombobox/PickerSkeleton';
import { usePickerDropdownPlacement } from '@atoms/PickerCombobox/usePickerDropdownPlacement';
import { IAbookSource, IAbookTab, IExternalSearchAbook, IGridModalAttributes, ISetValues } from '@models/Forms/IForms';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import { IAbookpickerRefActions } from './IAbookpickerRefActions';
import { AddressBookService } from '@services/AddressBookService';
import { EMPTY_DISPLAY_PLACEHOLDER } from '@srm/ui/theme/forms';
import { FieldA11yContext } from '@srm/ui/context/FieldA11yContext';

export interface IAbookpickerProps {
    /** Ссылка на поле-триггер — фокус поля формы (`setFocus`, ошибка валидации) */
    ref?: React.Ref<HTMLInputElement>;
    /** Имя поля */
    name?: string;
    /** */
    isMultiple: boolean;
    /**
     * При isMultiple=true — рекурсивное выделение узлов в TreeMode
     */
    recursive?: boolean;
    /** */
    title: string;
    /** зависимые поля*/
    setValues?: ISetValues;
    /** колонки для грида */
    gridAttribute?: IGridModalAttributes;
    /** Текст Placeholder */
    placeholder?: string;
    /** Значение по умолчанию */
    defaultValue?: any;
    /** отображаемое значение */
    displayValue: string;
    /** Скрыть поле */
    disabled?: boolean;
    /** Только для просмотра */
    readOnly?: boolean;
    /** Функция изменения значения */
    onChange?: (value: IAddressBookData[]) => Promise<void>;
    /** Кастомная кнопка */
    children?: ReactNode | ReactNode[];
    /** Переводит инпут в невалидный статус */
    invalid?: boolean;
    /** Реф для пробрасывания действий по кастомным кнопкам */
    controlRef?: React.Ref<IAbookpickerRefActions>;
    /**
     *  Добавлять фокус при выборе дат
     * @default false
     *  */
    isFocusBorder?: boolean;
    formValues: string;

    /**
     * Метод получения formValues
     * */
    getFormValues?: () => string;

    /**
     * Вкладки адресной книги
     * */
    tabsSettings?: IAbookTab[];

    /**
     * Дополнительная настройка источника данных (выбор данных из реквизитов-таблиц и т.д.)
     * */
    source?: IAbookSource;

    externalSearch?: IExternalSearchAbook;
    /** @deprecated Нижний блок выбора теперь показывается по умолчанию. */
    showChips: boolean;
    /**
     *  Отображение иконки сброса значения
     * @default true
     *  */
    showClearIcon?: boolean;
    displayType?: 'text' | 'default';
    /** Редактор ячейки таблицы: компактное поле */
    insideGrid?: boolean;
    /** Пикер раскрывается сразу при монтировании — ячейка таблицы открывает его одним кликом */
    autoFocus?: boolean;
    /** Пикер закрыт: редактор ячейки таблицы живёт, пока он открыт */
    onCancel?: () => void;
    wordWrapEnabled?: boolean;
    docId?: string;
    showActionsBar?: boolean;
    /** Выбор в выпадающем списке с поиском (вкладки-списки); окно адресной книги открывается из него. */
    isSelectMode?: boolean;
    /** Подписи пустых полей формы, от которых зависят вкладки (`formValues`). */
    getEmptyDependencies?: () => string[];
}

// FIXME: открытие окна с клавиатуры (в режиме списка оно есть)
const Abookpicker: React.FC<IAbookpickerProps> = ({
    ref,
    name = 'Abookpicker',
    placeholder = 'Выберите значение',
    defaultValue,
    displayValue,
    gridAttribute,
    invalid = false,
    disabled = false,
    readOnly = false,
    insideGrid = false,
    autoFocus,
    onCancel,
    onChange,
    children,
    title,
    isMultiple,
    recursive,
    controlRef,
    formValues,
    isFocusBorder = false,
    tabsSettings,
    source,
    externalSearch,
    showChips,
    showClearIcon = false,
    displayType = 'default',
    getFormValues = undefined,
    wordWrapEnabled,
    docId,
    showActionsBar,
    isSelectMode = false,
    getEmptyDependencies,
}: IAbookpickerProps) => {
    useImperativeHandle(controlRef, () => ({
        reset: () => {},
        unSelect: () => {},
        clearSelection: () => {},
    }));

    // -------------------------------------------------------------------------------------------------------------------

    const AbookpickerRef = useRef<HTMLDivElement>(null);

    const [showAbook, toggleAbook] = useState<boolean>(false);
    const [showDropdown, setShowDropdown] = useState(false);
    // символ, напечатанный на закрытом поле, — начало поиска в списке
    const [initialQuery, setInitialQuery] = useState<string>();
    const triggerInput = useRef<HTMLInputElement | null>(null);
    const inputRef = useMergedRef(ref, triggerInput);

    // Дерево подразделений — только в окне: в списке вкладки-списки. Таблица-источник и свой триггер — по-старому.
    const selectMode = isSelectMode && !readOnly && !disabled && !source?.table && !children;
    const dropdownTabs = useMemo(() => tabsSettings?.filter((tab) => tab.mode !== 'tree') ?? [], [tabsSettings]);
    // Вкладки с условиями показа приходят не сразу: до тех пор поле — тоже список (со скелетом), иначе клик открыл
    // бы окно, а смена режима перемонтировала бы поле. Окно вместо списка — только когда списков среди вкладок нет.
    const isSelectDropdown = selectMode && (!tabsSettings || dropdownTabs.length > 0);
    const dropdownPlacement = usePickerDropdownPlacement(AbookpickerRef, showDropdown && isSelectDropdown);

    const closeDropdown = () => {
        setShowDropdown(false);
        setInitialQuery(undefined);
    };

    const focusTrigger = () => requestAnimationFrame(() => triggerInput.current?.focus({ preventScroll: true }));

    // закрыть список без перехода в окно — отмена: редактор ячейки таблицы закрывается вместе с ним
    const dismissDropdown = () => {
        closeDropdown();
        onCancel?.();
    };

    // Выбор, Escape и Tab в списке: фокус — снова в поле (портал списка уже снят, сам Popover его не вернул бы).
    // Клик вне списка закрывает его без этого — фокус остаётся там, куда кликнули.
    const closeDropdownToField = () => {
        dismissDropdown();
        focusTrigger();
    };

    const openBook = () => {
        closeDropdown();
        toggleAbook(true);
    };

    // окно открыто из списка, которого уже нет, — фокус возвращаем полю
    const closeBook = () => {
        toggleAbook(false);
        onCancel?.();
        if (isSelectDropdown) focusTrigger();
    };

    // вкладки разрешились в одни деревья — клик по полю, ждавший их под скелетом, не должен всплыть списком позже
    useEffect(() => {
        if (!isSelectDropdown) setShowDropdown(false);
    }, [isSelectDropdown]);

    // ячейка таблицы: пикер раскрыт сразу; вкладки разрешились в одни деревья — вместо списка окно
    useEffect(() => {
        if (!autoFocus || readOnly || disabled) return;
        if (isSelectDropdown) setShowDropdown(true);
        else toggleAbook(true);
    }, [autoFocus, readOnly, disabled, isSelectDropdown]);

    const openPicker = () => {
        if (!isSelectDropdown) toggleAbook((prev) => !prev);
        else if (showDropdown) dismissDropdown();
        else setShowDropdown(true);
    };

    // -------------------------------------------------------------------------------------------------------------------

    const [inputValue, setInputValue] = useState<number[]>();
    const [localDisplayValue, setLocalDisplayValue] = useState<string>(displayValue);

    useEffect(() => {
        setLocalDisplayValue(displayValue);
    }, [displayValue]);

    useEffect(() => {
        if (!defaultValue) {
            // поле очищено (сняты все отметки, сброс каскадом) — следующее открытие не должно вернуть прежние
            setInputValue(undefined);
            return;
        }

        if (source?.table) {
            const keys = (defaultValue as any[])?.map((row: any) => parseInt(row[source.key]));
            if (keys) {
                setInputValue(keys);
            }
        } else {
            if (defaultValue.toString().indexOf('|') > 0) {
                const arr = [] as number[];
                const arrStr = [] as string[];
                defaultValue
                    .toString()
                    .split('|')
                    .forEach((item: string) => {
                        arr.push(Number(item));
                        arrStr.push(item);
                    });

                setInputValue(arr);
            } else if (Array.isArray(defaultValue)) {
                const keys = defaultValue?.map((row: any) => parseInt(row));
                if (keys) {
                    setInputValue(keys);
                    AddressBookService.fetchAbookItemsByKeys(keys.map((x) => x.toString())).then((response) => {
                        const data = response.data.map((x) => x.name).join(', ');
                        setLocalDisplayValue(data);
                    });
                }
            } else {
                const arr = [] as number[];
                arr.push(Number(defaultValue));

                setInputValue(arr);
            }
        }
    }, [defaultValue]);

    const clearHandler = async () => {
        setInputValue(undefined);
        onChange && (await onChange([]));
    };

    // read-only/disabled = display-only триггер: приглашающий плейсхолдер лжёт, пустое
    // поле показывает прочерк-маркер вместо «Выберите значение» (паритет с Dictpicker).
    const resolvedPlaceholder = readOnly || disabled ? EMPTY_DISPLAY_PLACEHOLDER : placeholder;

    // Одиночный сотрудник в поле со списком — с аватаром, как в строке списка
    const valueKey = inputValue?.length === 1 ? inputValue[0] : undefined;
    const valueAvatar =
        isSelectDropdown && valueKey && localDisplayValue && dropdownTabs.every(isPersonTab) ? (
            <AbookItemAvatar item={{ key: valueKey, name: localDisplayValue }} size={24} />
        ) : undefined;

    const triggerNode = (
        <div
            className={clsx('rf-abookpicker__input-wrapper', {
                'rf-abookpicker__input-wrapper--disabled': disabled,
                'rf-abookpicker__input-wrapper--readonly': readOnly,
            })}
            onClick={!readOnly ? openPicker : undefined}
            // Список открывается с клавиатуры (Enter, пробел, стрелки), напечатанный символ — с поиском
            onKeyDown={
                isSelectDropdown
                    ? (e: React.KeyboardEvent) => {
                          if (e.key === 'Escape' && showDropdown) {
                              e.stopPropagation();
                              dismissDropdown();
                              return;
                          }
                          if (showDropdown) return;
                          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                              e.preventDefault();
                              setShowDropdown(true);
                          } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                              e.preventDefault();
                              setInitialQuery(e.key);
                              setShowDropdown(true);
                          }
                      }
                    : undefined
            }
        >
            {children ||
                (displayType == 'default' ? (
                    <Input
                        ref={inputRef}
                        name={name}
                        value={localDisplayValue}
                        placeholder={resolvedPlaceholder}
                        disabled={disabled}
                        readOnly={true}
                        // Интерактивный триггер: role=combobox — вид обычного Input из темы.
                        role={readOnly ? undefined : 'combobox'}
                        invalid={invalid}
                        autoFocus={autoFocus}
                        leftSection={valueAvatar}
                        // В rightSection ДВЕ иконки (clear + menu) — Mantine-секция фикс-ширины
                        // под одну; расширяем под две (SCSS раскладывает их flex-рядом).
                        rightSectionWidth={showClearIcon ? 56 : 32}
                        rightSection={
                            !readOnly && !disabled ? (
                                <>
                                    {showClearIcon && (
                                        <div className={'rf-abookpicker__calendar-chevron'}>
                                            <MdClose
                                                size="18"
                                                onClick={async (e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    await clearHandler();
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className={'rf-abookpicker__calendar-chevron'}>
                                        {isSelectDropdown ? (
                                            <MdOutlineExpandMore
                                                size={20}
                                                className={clsx(
                                                    'rf-abookpicker__expand',
                                                    showDropdown && 'rf-abookpicker__expand--open',
                                                )}
                                            />
                                        ) : (
                                            <MdMenu size={18} />
                                        )}
                                    </div>
                                </>
                            ) : undefined
                        }
                    />
                ) : (
                    <div className="rf-abookpicker__displayType-text ">{localDisplayValue}</div>
                ))}
        </div>
    );

    return (
        <div
            className={clsx(
                'rf-abookpicker',
                isFocusBorder && 'rf-abookpicker__focus-border',
                disabled && 'rf-abookpicker--disabled',
                insideGrid && 'rf-abookpicker-ingrid',
            )}
            ref={AbookpickerRef}
        >
            {selectMode ? (
                // Список — Mantine Popover, как select-режим справочника: портал, позиционирование, дисмисс
                <Popover
                    opened={showDropdown && isSelectDropdown}
                    onChange={(opened) => (opened ? setShowDropdown(true) : dismissDropdown())}
                    withinPortal
                    keepMounted={false}
                    transitionProps={{ duration: 0 }}
                    zIndex={1000}
                    {...dropdownPlacement}
                    width="target"
                    floatingStrategy="fixed"
                    styles={{ dropdown: { minWidth: 360 } }}
                    returnFocus
                    // Escape в списке обрабатывает AbookCombobox
                    closeOnEscape={false}
                    offset={8}
                >
                    <Popover.Target>{triggerNode}</Popover.Target>
                    <Popover.Dropdown className="rf-select__dropdown rf-dropdown__content" data-testid="rf-dropdown">
                        {dropdownTabs.length ? (
                            <FieldA11yContext value={null}>
                                <AbookCombobox
                                    tabs={dropdownTabs}
                                    isMultiple={isMultiple}
                                    selected={inputValue}
                                    title={title}
                                    getFormValues={getFormValues ?? (() => formValues)}
                                    initialQuery={initialQuery}
                                    getEmptyDependencies={getEmptyDependencies}
                                    onSubmit={onChange!}
                                    onClose={closeDropdownToField}
                                    onOpenBook={openBook}
                                />
                            </FieldA11yContext>
                        ) : (
                            <div className="rf-picker-combobox" aria-busy="true">
                                <PickerSkeleton avatar />
                            </div>
                        )}
                    </Popover.Dropdown>
                </Popover>
            ) : (
                triggerNode
            )}

            {showAbook && (
                // поля окна выбора — не поле группы формы: её подпись и ошибка относятся к триггеру
                <FieldA11yContext value={null}>
                    <AbookpickerModal
                        selected={inputValue!}
                        isMultiple={isMultiple}
                        recursive={recursive}
                        title={title}
                        externalSearch={externalSearch}
                        formValues={formValues}
                        getFormValues={getFormValues}
                        gridAttribute={gridAttribute}
                        onSubmit={onChange!}
                        onClose={closeBook}
                        tabsSettings={tabsSettings}
                        showChips={showChips}
                        wordWrapEnabled={wordWrapEnabled}
                        docId={docId}
                        showActionsBar={showActionsBar}
                    />
                </FieldA11yContext>
            )}
        </div>
    );
};

export default Abookpicker;
